import React, { useEffect, useState } from 'react';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { Label, Button, InputText } from 'design-system/Form';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import useFetchBatches from 'react-scale/containers/corp/backfills/batch/hooks/useFetchBatches';
// TODO: Migrate module so that you don’t import from the corp project in a global module
/* eslint-disable restrict-imports/restrict-imports */
import SelectProject from 'corp/components/UI/SelectProject';
/* eslint-disable restrict-imports/restrict-imports */
import Select from 'corp/components/UI/Select';
import isEmail from 'isemail';
import { Input } from '@material-ui/core';

const description = `
### Description:
- This tool allows you to "forgive" taskers and recover their attempts from any individuals that were blocked either due to the automated quality script or the manual quality checks.
- Select the project and batch, and then click the button to forgive.
NOTE: 🚨 All pay backfills, positive or negative, should be approved in advance by the pay team (Diego de Unanue & Nicholas Dell'Omo) 🚨

##### Author: Carlos Gonzalez
---
`;

interface SelectOption {
  label: string;
  value: any;
}

const validSubAddresses = ['outlier'];

const ForgiveTaskers = () => {
  const [project, setProject] = useState<SelectOption | null>();
  const [batch, setBatch] = useState<SelectOption | null>();
  const [taskers, setTaskers] = useState<string[] | null>();
  const [payout, setPayout] = useState<number | null>(null);
  const [query, setQuery] = useState<any>({});
  const { batches, error, isLoading } = useFetchBatches(query);
  const [batchOptions, setBatchOptions] = useState<SelectOption[] | null>();
  const [inputError, setInputError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>();
  const enabled = project && batch && payout !== null;

  useEffect(() => {
    if (project) {
      setQuery({ project: project.value });
      setBatch(null);
      setBatchOptions(null);
    }
  }, [project]);

  useEffect(() => {
    setBatchOptions(
      batches.map(batch => {
        return { label: batch.name, value: batch._id };
      }),
    );
  }, [batches]);

  const handleProject = async (project: SelectOption) => {
    setProject(project);
  };

  const handleBatch = async (batch: SelectOption) => {
    setBatch(batch);
  };

  const handleForgive = async () => {
    setLoading(true);
    setMessage('');
    // eslint-disable-next-line no-console
    console.log(`Forgiving batch ${batch?.value}: "${batch?.label}" -> "${taskers}"`);

    try {
      const res = await apiFetch(null, 'corp-api/bee-tts-utils/forgive', {
        method: 'POST',
        body: { batchId: batch?.value, projectId: project?.value, taskersEmails: taskers, payout },
      });

      setProject(null);
      setBatch(null);
      setBatchOptions(null);
      setTaskers(null);
      setPayout(null);
      setLoading(false);
      setInputError('');
      setMessage(res.message);
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
      setLoading(false);
    }
  };

  const selectBatch = () => {
    if (!project) return 'Select project';
    if (project && error) return `Error fetching batches, please try again later... (${error}}`;
    if (project && isLoading) return 'Loading batches...';
    return (
      <Select value={batch} disabled={!project} onChange={handleBatch} options={batchOptions} />
    );
  };

  const onChangeTaskers = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newTaskers = event.target.value.split(',').map(tasker => {
      const emailWhole = tasker.trim();
      const [email, subaddress] = emailWhole.split('+');

      if (subaddress && !validSubAddresses.includes(subaddress)) {
        setInputError(`subaddressing after + sign must be one of ${validSubAddresses.join(', ')}`);
        return tasker.trim();
      }

      const invalidEmail = !isEmail.validate(email, { minDomainAtoms: 2 });
      setInputError(invalidEmail ? 'Invalid email format' : '');
      return tasker.trim();
    });
    setTaskers(newTaskers);
  };

  const selectTaskers = () => {
    if (!enabled) return 'Select project, batches and payout';
    if (enabled && isLoading) return 'Loading taskers...';
    return (
      <div className="inputComponent">
        <InputText
          placeholder="Taskers Emails (comma separated)"
          value={taskers?.join(',')}
          onChange={onChangeTaskers}
        />
        {enabled && !!inputError && <h4>{inputError}</h4>}
      </div>
    );
  };

  const buttonText = () => {
    if (!project) return 'Select project';
    if (!batch) return 'Select batch';
    if (!taskers || !!inputError) return 'Input taskers';
    return 'Forgive taskers';
  };

  const renderOutput = () => {
    return (
      <div className="response-markdown">
        <SanitizedMarkdown text={message || ''} />
        <style jsx global>{`
          .response-markdown tr {
            border: 1px solid rgba(0, 0, 0, 0.3);
          }

          .response-markdown tr:nth-child(even) {
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgb(0, 0, 0, 0.3);
            padding: 6px 10px;
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgba(255, 255, 255, 0.3);
            padding: 6px 10px;
            background-color: #222;
            color: white;
          }

          .response-markdown td {
            border: 1px solid rgba(0, 0, 0, 0.3);
            padding: 10px;
          }
        `}</style>
      </div>
    );
  };

  const onChangePayout = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setPayout(Number(event.target.value));
  };

  return (
    <div className="root">
      <section>
        <SanitizedMarkdown text={description} />
      </section>

      <section className="two-columns">
        <section>
          <Label>Project:</Label>
          <SelectProject value={project} onChange={handleProject} />
        </section>
        <section className={project ? '' : 'disabled'}>
          <Label>Batch:</Label>
          {selectBatch()}
        </section>
      </section>
      <section className="two-columns">
        <section className={project && batch ? '' : 'disabled'}>
          <Label>Payout:</Label>
          <Input
            value={payout}
            margin="dense"
            onChange={onChangePayout}
            disabled={!project || !batches}
            inputProps={{
              step: 1,
              min: 0,
              type: 'number',
            }}
          />
        </section>
        <section className={enabled ? '' : 'disabled'}>
          <Label>Taskers:</Label>
          {selectTaskers()}
        </section>
      </section>
      <br />
      <section>
        <Button
          primary
          disabled={!enabled || !taskers || !!inputError || loading}
          requireConfirmation
          onClick={handleForgive}
        >
          {buttonText()}
        </Button>
        {message && renderOutput()}
      </section>
      <style jsx>{`
        .root .two-columns {
          display: flex;
        }
        .root .two-columns section {
          flex-basis: 50%;
        }
        .root .two-columns section.disabled {
          color: rgba(0, 0, 0, 0.3);
        }
      `}</style>
    </div>
  );
};

export default ForgiveTaskers;
